import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import Reveal from "@/components/Reveal";

const FACTS = [
  "Canon & Xerox digital print fleet",
  "70–350 GSM white paper stock",
  "GST invoicing for bulk orders",
  "Express turn-around on request",
];

export default function About() {
  const imgWrap = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrap, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);

  return (
    <section id="about" className="py-20 lg:py-28" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5" ref={imgWrap}>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="overflow-hidden border border-edge rounded-sm aspect-[4/5]">
              <motion.img
                src="https://images.unsplash.com/photo-1581508512961-0e3b9524db40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Precision printing equipment on the press floor"
                style={{ y }}
                className="w-full h-[110%] object-cover saturate-[0.85]"
                loading="lazy"
                data-testid="about-press-image"
              />
            </div>
            <span className="absolute -top-3 -right-3 w-6 h-6 border-t-2 border-r-2 border-yel" aria-hidden="true" />
            <span className="absolute -bottom-3 -left-3 w-6 h-6 border-b-2 border-l-2 border-mag" aria-hidden="true" />
          </motion.div>
        </div>

        <div className="lg:col-span-7 lg:pl-8">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanbrand">
              / About us
            </span>
            <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-paper leading-[1.05]">
              A press built for
              <br /> deadlines.
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="mt-8 text-base sm:text-lg text-mist leading-relaxed max-w-xl">
              PRO PRINT SOLUTION LLP runs a high-volume digital print floor serving
              businesses, students, startups and coaching centres across Mumbai. We keep
              the machines humming, the paper white and the promises short — your files
              in the morning become bound documents by evening.
            </p>
          </Reveal>
          <Reveal delay={2}>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FACTS.map((fact) => (
                <li key={fact} className="flex items-center gap-3 border border-edge bg-surface rounded-sm px-4 py-3.5">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-cyanbrand/10 border border-cyanbrand/40 grid place-items-center">
                    <Check size={12} className="text-cyanbrand" />
                  </span>
                  <span className="text-sm text-paper/90">{fact}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
